import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Monogram, ProfilePhoto } from "@/components/AuthImage";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Heart, ChatCircle, Star, SealCheck, MapPin, GraduationCap, Briefcase, Phone, Envelope, Lock, Sparkle } from "@phosphor-icons/react";

export default function ProfileDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${id}`);
        setProfile(data);
      } catch (e) {
        toast.error("Profile not found");
        nav("/browse");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, nav]);

  const sendInterest = async () => {
    setSending(true);
    try {
      await api.post("/interests/send", { to_user_id: id });
      toast.success("Interest sent, in-shaa-Allah");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send interest");
    } finally {
      setSending(false);
    }
  };

  const shortlist = async () => {
    try {
      await api.post(`/shortlist/${id}`);
      toast.success("Added to shortlist");
    } catch (e) { toast.error("Failed"); }
  };

  const message = () => nav(`/messages/${id}`);

  if (loading || !profile) {
    return <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 text-brand-muted">Loading…</div>;
  }

  const isPaid = user?.plan && user.plan !== "free";

  return (
    <div className="max-w-[1200px] mx-auto px-6 md:px-10 py-10" data-testid={`profile-detail-${id}`}>
      {/* Header hero */}
      <div className="bg-white border border-brand-line p-8 md:p-12">
        <div className="flex flex-wrap items-center gap-8">
          <ProfilePhoto profile={profile} className="w-32 h-32 flex-shrink-0 rounded-full" size="text-5xl" />
          <div className="flex-1 min-w-0">
            <div className="label-eyebrow text-brand-terracotta">Profile {profile.id.slice(-6).toUpperCase()}</div>
            <h1 className="font-serif text-5xl lg:text-6xl tracking-tighter mt-2 text-brand-ink">
              {profile.name}
              {profile.age ? <span className="text-brand-muted font-light"> , <span className="num-lining">{profile.age}</span></span> : null}
            </h1>
            <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-brand-muted">
              {profile.city && <span className="flex items-center gap-1"><MapPin size={14} /> {[profile.city, profile.state, profile.country].filter(Boolean).join(", ")}</span>}
              {profile.profession && <span className="flex items-center gap-1"><Briefcase size={14} /> {profile.profession}</span>}
              {profile.education && <span className="flex items-center gap-1"><GraduationCap size={14} /> {profile.education}</span>}
              {profile.verified && <span className="flex items-center gap-1 text-brand-terracotta"><SealCheck size={14} weight="fill" /> Verified</span>}
              <VerifiedBadge verifications={profile.verifications} />
              {profile.ai_verification?.verdict === "verified" && (
                <span
                  className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest bg-brand-ink text-brand-gold px-2.5 py-1 rounded-full"
                  data-testid={`profile-ai-verified-${profile.id}`}
                  title={`AI-Verified · ${profile.ai_verification.overall_score}/100`}
                >
                  <Sparkle size={11} weight="fill" /> AI-Verified · {profile.ai_verification.overall_score}/100
                </span>
              )}
            </div>
          </div>
        </div>

        {profile.about_me && (
          <p className="mt-8 font-serif italic text-2xl text-brand-ink leading-snug border-l-2 border-brand-gold pl-6">
            "{profile.about_me}"
          </p>
        )}

        <div className="mt-8 flex flex-wrap gap-3">
          <Button
            onClick={sendInterest} disabled={sending || profile.id === user?.id}
            data-testid="send-interest-btn"
            className="rounded-full bg-brand-terracotta hover:bg-brand-terracotta/90 text-white px-6"
          >
            <Heart size={16} className="mr-2" weight="fill" /> Send Interest
          </Button>
          <Button onClick={shortlist} data-testid="shortlist-btn" variant="outline" className="rounded-full border-brand-line px-6">
            <Star size={16} className="mr-2" /> Shortlist
          </Button>
          <Button onClick={message} data-testid="message-btn" variant="outline" className="rounded-full border-brand-line px-6">
            <ChatCircle size={16} className="mr-2" /> Message
          </Button>
        </div>
      </div>

      {/* Contact info gated */}
      <div className="mt-8 border border-brand-line p-6 bg-white">
        <div className="label-eyebrow text-brand-muted">Contact</div>
        {isPaid || user?.role === "admin" ? (
          <div className="mt-3 space-y-2 text-brand-ink">
            {profile.email && <div className="flex items-center gap-2"><Envelope size={16} className="text-brand-terracotta" /> {profile.email}</div>}
            {profile.phone && <div className="flex items-center gap-2"><Phone size={16} className="text-brand-terracotta" /> {profile.phone}</div>}
            {!profile.email && !profile.phone && <div className="text-brand-muted text-sm">Contact info not provided.</div>}
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-3">
            <Lock size={18} className="text-brand-gold" />
            <div className="text-sm text-brand-muted">Contact details reveal for members. <a href="/plans" className="text-brand-terracotta underline underline-offset-4">Upgrade</a></div>
          </div>
        )}
      </div>

      {/* Details tabs */}
      <Tabs defaultValue="basic" className="mt-8">
        <TabsList className="bg-transparent border-b border-brand-line rounded-none p-0 w-full justify-start gap-6 overflow-x-auto">
          <TabsTrigger value="basic" data-testid="tab-basic" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Basics</TabsTrigger>
          <TabsTrigger value="deen" data-testid="tab-deen" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Deen</TabsTrigger>
          <TabsTrigger value="family" data-testid="tab-family" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Family</TabsTrigger>
          <TabsTrigger value="career" data-testid="tab-career" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Career</TabsTrigger>
          <TabsTrigger value="partner" data-testid="tab-partner" className="rounded-none border-b-2 border-transparent data-[state=active]:border-brand-terracotta data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 label-eyebrow">Partner</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="pt-6 grid grid-cols-2 gap-x-6 gap-y-5">
          <DL label="Height" value={profile.height_cm ? `${profile.height_cm} cm` : "—"} />
          <DL label="Marital status" value={profile.marital_status || "—"} />
          <DL label="Mother tongue" value={profile.mother_tongue || "—"} />
          <DL label="Diet" value={profile.diet || "—"} />
        </TabsContent>

        <TabsContent value="deen" className="pt-6 grid grid-cols-2 gap-x-6 gap-y-5">
          <DL label="Sect" value={profile.community || "—"} />
          <DL label="Sub-sect / Maslak" value={profile.sub_sect || "—"} />
          <DL label="Salah / Namaz" value={profile.namaz || "—"} />
          <DL label="Hijab / Beard" value={profile.observance || "—"} />
          <DL label="Read Quran" value={profile.reads_quran || "—"} />
        </TabsContent>

        <TabsContent value="family" className="pt-6 grid grid-cols-2 gap-x-6 gap-y-5">
          <DL label="Family type" value={profile.family_type || "—"} />
          <DL label="Family status" value={profile.family_status || "—"} />
          <DL label="Father's occupation" value={profile.father_occupation || "—"} />
          <DL label="Mother's occupation" value={profile.mother_occupation || "—"} />
          <DL label="Siblings" value={profile.siblings || "—"} />
        </TabsContent>

        <TabsContent value="career" className="pt-6 grid grid-cols-2 gap-x-6 gap-y-5">
          <DL label="Education" value={profile.education || "—"} />
          <DL label="Profession" value={profile.profession || "—"} />
          <DL label="Annual income" value={profile.annual_income || "—"} />
          <DL label="Location" value={[profile.city, profile.state, profile.country].filter(Boolean).join(", ") || "—"} />
        </TabsContent>

        <TabsContent value="partner" className="pt-6">
          <div className="grid grid-cols-2 gap-x-6 gap-y-5">
            <DL label="Preferred age" value={profile.partner_age_min || profile.partner_age_max ? `${profile.partner_age_min || "?"}–${profile.partner_age_max || "?"}` : "—"} />
            <DL label="Height (min)" value={profile.partner_height_min_cm ? `${profile.partner_height_min_cm} cm` : "—"} />
            <DL label="Education" value={profile.partner_education || "—"} />
            <DL label="Sect" value={profile.partner_community || "—"} />
          </div>
          {profile.partner_expectations && (
            <div className="mt-6">
              <div className="label-eyebrow text-brand-muted">Expectations</div>
              <p className="mt-2 text-brand-ink leading-relaxed">{profile.partner_expectations}</p>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}

function DL({ label, value }) {
  return (
    <div>
      <div className="label-eyebrow text-brand-muted">{label}</div>
      <div className="mt-1 text-brand-ink">{value}</div>
    </div>
  );
}
